import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts'
import { formatCurrency, formatDate } from '@utils/format.js'
import { CHART_COLORS, type LancamentoItem } from './financeiroTypes.js'

type BarDatum = { cat: string; realizado: number; previsto: number }
type PieDatum = { name: string; value: number }

type Props = {
  barData: BarDatum[]
  pieData: PieDatum[]
  top10: LancamentoItem[]
  fornList: [string, number][]
}

export function DashboardCharts({ barData, pieData, top10, fornList }: Props) {
  return (
    <>
      {barData.length > 0 && (
        <section className='cf3-block' aria-labelledby='dash-bar-title'>
          <h3 id='dash-bar-title' className='cf3-subsection-title'>
            Previsto vs Realizado
          </h3>
          <div className='cf3-fin-dash__chart-card'>
            <div className='cf3-fin-dash__legend'>
              <span>
                <span className='cf3-fin-dash__legend-dot cf3-fin-dash__legend-dot--previsto' />
                Previsto
              </span>
              <span>
                <span className='cf3-fin-dash__legend-dot cf3-fin-dash__legend-dot--realizado' />
                Realizado
              </span>
            </div>
            <ResponsiveContainer width='99%' height={Math.max(200, barData.length * 44)}>
              <BarChart
                data={barData}
                layout='vertical'
                margin={{ top: 4, right: 32, left: 4, bottom: 4 }}
              >
                <XAxis
                  type='number'
                  tick={{ className: 'cf3-fin-dash__tick cf3-fin-dash__tick--num' }}
                  tickFormatter={(v) => (v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`)}
                />
                <YAxis
                  type='category'
                  dataKey='cat'
                  width={120}
                  tick={{ className: 'cf3-fin-dash__tick cf3-fin-dash__tick--cat' }}
                />
                <Tooltip formatter={(v) => [formatCurrency(Number(v ?? 0))]} />
                <Bar
                  dataKey='previsto'
                  name='Previsto'
                  fill='#C2BDB0'
                  radius={[0, 4, 4, 0]}
                  maxBarSize={20}
                />
                <Bar
                  dataKey='realizado'
                  name='Realizado'
                  fill='#E8A830'
                  radius={[0, 4, 4, 0]}
                  maxBarSize={20}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {pieData.length > 0 && (
        <section className='cf3-block' aria-labelledby='dash-pie-title'>
          <h3 id='dash-pie-title' className='cf3-subsection-title'>
            Distribuição por Categoria
          </h3>
          <div className='cf3-fin-dash__chart-card'>
            <ResponsiveContainer width='99%' height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey='value'
                  nameKey='name'
                  cx='50%'
                  cy='45%'
                  outerRadius={100}
                  innerRadius={40}
                  label={({ percent }) =>
                    (percent ?? 0) > 0.04 ? `${((percent ?? 0) * 100).toFixed(0)}%` : ''
                  }
                  labelLine={false}
                >
                  {pieData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length] ?? '#948E80'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => [formatCurrency(Number(v ?? 0))]} />
                <Legend
                  formatter={(value) => <span className='cf3-fin-dash__legend-label'>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {top10.length > 0 && (
        <section className='cf3-block' aria-labelledby='dash-top-title'>
          <h3 id='dash-top-title' className='cf3-subsection-title'>
            Top {top10.length} Maiores Lançamentos
          </h3>
          <div className='cf3-fin-dash__table-wrap'>
            <table className='cf3-fin-dash__table' aria-label='Top 10 maiores lançamentos'>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th>Data</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                {top10.map((l, i) => (
                  <tr key={l.id}>
                    <td className='cf3-fin-dash__td--rank'>{i + 1}</td>
                    <td className='cf3-fin-dash__td--titulo'>{l.titulo}</td>
                    <td className='cf3-fin-dash__td--cat'>{l.categoria}</td>
                    <td className='cf3-fin-dash__td--date'>{formatDate(l.data)}</td>
                    <td className='cf3-fin-dash__td--valor'>{formatCurrency(l.valor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {fornList.length > 0 && (
        <section className='cf3-block' aria-labelledby='dash-forn-title'>
          <h3 id='dash-forn-title' className='cf3-subsection-title'>
            Total por Fornecedor
          </h3>
          <ul className='cf3-fin-dash__forn-list' aria-label='Total por fornecedor'>
            {fornList.map(([forn, val], i) => (
              <li key={forn} className='cf3-fin-dash__forn-item'>
                <span className='cf3-fin-dash__forn-rank'>{i + 1}</span>
                <span className='cf3-fin-dash__forn-nome'>{forn}</span>
                <span className='cf3-fin-dash__forn-valor'>{formatCurrency(val)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}
